import React, { useState, useEffect } from "react";
import { DateTime } from "luxon";
import { McModal } from "@maersk-global/mds-react-wrapper/components-core/mc-modal";
import { McButton } from "@maersk-global/mds-react-wrapper";
import { McTabBar, McTab } from "@maersk-global/mds-react-wrapper";
import DocumentsTab from "./DocumentsTab";
import { fetchDocuments } from "./apiHelpers";

const downloadCSV = (data, filename) => {
  const headers = Object.keys(data[0]);
  const csvContent = [
    headers.join(","),
    ...data.map(row => headers.map(header => {
      const value = row[header];
      if (value === null || value === undefined) return "";
      const stringValue = String(value);
      if (stringValue.includes(",") || stringValue.includes('"') || stringValue.includes("\n")) {
        return `"${stringValue.replace(/"/g, '""')}"`;
      }
      return stringValue;
    }).join(","))
  ].join("\n");

  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
  const link = document.createElement("a");
  const url = URL.createObjectURL(blob);
  link.setAttribute("href", url);
  link.setAttribute("download", filename);
  link.style.visibility = "hidden";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

export default function StatisticsModal({
  isOpen,
  loading,
  selectedRow,
  statisticsData,
  incidentCounts,
  oldestIncidentDate,
  postmortemPreventiveMeasures = [],
  onClose,
  userRole,
  userEmail,
}) {
  const [expandedIncidents, setExpandedIncidents] = useState(false);
  const [activeTab, setActiveTab] = useState(0); // Changed to index-based
  const [documents, setDocuments] = useState([]);
  const [documentsLoading, setDocumentsLoading] = useState(false);

  // Reset to statistics tab when modal opens
  useEffect(() => {
    if (isOpen) {
      setActiveTab(0);
    }
  }, [isOpen]);

  // Load documents only when switching to documents tab
  useEffect(() => {
    if (isOpen && selectedRow && activeTab === 1) { // Documents tab index
      loadDocuments();
    }
  }, [activeTab]);

  const loadDocuments = async () => {
    if (!selectedRow?.app_name) return;
    
    setDocumentsLoading(true);
    try {
      const channel = selectedRow.status === "Green" ? "green" : "red";
      const docs = await fetchDocuments(selectedRow.app_name, channel);
      setDocuments(docs);
    } catch (error) {
      console.error("Error loading documents:", error);
      setDocuments([]);
    } finally {
      setDocumentsLoading(false);
    }
  };

  const handleTabChange = (event) => {
    const newTab = event.detail;
    console.log("Tab change:", newTab);
    setActiveTab(newTab);
  };

  if (!isOpen) return null;

  const calculateDuration = (start) => {
    if (!start) return "N/A";
    const startDate = DateTime.fromISO(start);
    if (!startDate.isValid) return "N/A";
    return `${Math.ceil(DateTime.now().diff(startDate, "days").days)} days`;
  };

  const calculateProgress = (start, end) => {
    if (!start || !end) return 0;
    const startDate = DateTime.fromISO(start);
    const endDate = DateTime.fromISO(end);
    if (!startDate.isValid || !endDate.isValid) return 0;
    const totalDays = Math.ceil(endDate.diff(startDate, "days").days);
    const daysPassed = Math.ceil(DateTime.now().diff(startDate, "days").days);
    return totalDays > 0 ? Math.min(100, Math.round((daysPassed / totalDays) * 100)) : 0;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "N/A";
    const date = DateTime.fromISO(dateStr);
    return date.isValid ? date.toFormat("d/M/yyyy") : "N/A";
  };

  const daysSince = (dateStr) => {
    if (!dateStr) return "N/A";
    const date = DateTime.fromISO(dateStr);
    return date.isValid ? Math.ceil(DateTime.now().diff(date, "days").days) : "N/A";
  };

  const daysUntil = (dateStr) => {
    if (!dateStr) return "N/A";
    const date = DateTime.fromISO(dateStr);
    return date.isValid ? Math.max(0, Math.ceil(date.diff(DateTime.now(), "days").days)) : "N/A";
  };

  const formatIncidentDate = (dateStr) => {
    if (!dateStr) return "N/A";
    
    // Check if already in dd-MM-yyyy format (e.g., "01-07-2025")
    if (/^\d{2}-\d{2}-\d{4}$/.test(dateStr)) {
      // Validate it's a real date by parsing
      const date = DateTime.fromFormat(dateStr, "dd-MM-yyyy");
      return date.isValid ? dateStr : "N/A";
    }
    
    // Handle format: "2025-10-13 11:03:40" or ISO format
    let date;
    if (dateStr.includes(" ")) {
      // Parse "YYYY-MM-DD HH:mm:ss" format
      const [datePart] = dateStr.split(" ");
      date = DateTime.fromISO(datePart);
    } else {
      date = DateTime.fromISO(dateStr);
    }
    return date.isValid ? date.toFormat("dd-MM-yyyy") : "N/A";
  };

  const determineGreenChannelReadiness = () => {
    const p1p2Count = incidentCounts?.total || 0;
    const hasP1P2Incidents = p1p2Count > 0;
    
    // Check if all postmortem preventive measures are closed or done
    const allMeasuresComplete = postmortemPreventiveMeasures && postmortemPreventiveMeasures.length > 0
      ? postmortemPreventiveMeasures.every(measure => 
          measure.status?.toLowerCase() === "closed" || measure.status?.toLowerCase() === "done"
        )
      : true; // If no measures exist, consider them complete
    
    if (!hasP1P2Incidents && allMeasuresComplete) {
      return {
        ready: true,
        message: "Ready to be moved to green channel",
        reasons: ["No P1/P2 incidents after being added to red channel", "All postmortem preventive measures have been closed"]
      };
    } else {
      let reasons = [];
      if (hasP1P2Incidents) {
        reasons.push(`${p1p2Count} P1/P2 incident${p1p2Count > 1 ? 's' : ''} since app is on red channel`);
      }
      if (!allMeasuresComplete) {
        const pendingMeasures = postmortemPreventiveMeasures.filter(measure => 
          measure.status?.toLowerCase() !== "closed" && measure.status?.toLowerCase() !== "done"
        );
        reasons.push(`${pendingMeasures.length} postmortem preventive measure${pendingMeasures.length > 1 ? 's' : ''} not closed`);
      }
      return {
        ready: false,
        message: "Not ready to move to green channel",
        reasons: reasons
      };
    }
  };

  const handleDownloadStatistics = () => {
    if (!selectedRow) return;

    // Build CSV content manually for better formatting
    let csvContent = "Statistics Report\n\n";
    
    // Application Information Section
    csvContent += "APPLICATION INFORMATION\n";
    csvContent += `App ID,${selectedRow.app_id || "N/A"}\n`;
    csvContent += `App Instance Name,${selectedRow.app_name || "N/A"}\n`;
    csvContent += `Latest Incident,${selectedRow.latest_incident || "N/A"}\n`;
    csvContent += `Status,${selectedRow.status ? `${selectedRow.status} Channel` : "N/A"}\n\n`;
    
    // Green Channel Readiness Section
    const readiness = determineGreenChannelReadiness();
    csvContent += "GREEN CHANNEL READINESS\n";
    csvContent += `Status,${readiness.message}\n`;
    if (readiness.reasons && readiness.reasons.length > 0) {
      readiness.reasons.forEach((reason, index) => {
        csvContent += `Reason ${index + 1},${reason}\n`;
      });
    }
    csvContent += `\n`;
    
    // Timeline Section
    csvContent += "TIMELINE\n";
    csvContent += `Oldest Incident Date,${oldestIncidentDate ? oldestIncidentDate.replace(/-/g, "/") : "N/A"}\n`;
    csvContent += `Added to Red Channel Date,${formatDate(selectedRow._originalData?.added_to_red_channel)}\n`;
    csvContent += `Final Review Date,${formatDate(selectedRow._originalData?.final_review)}\n`;
    csvContent += `Duration on Red Channel,${calculateDuration(selectedRow._originalData?.added_to_red_channel)}\n`;
    const elapsedDays = selectedRow._originalData?.added_to_red_channel ? Math.ceil(DateTime.now().diff(DateTime.fromISO(selectedRow._originalData.added_to_red_channel), "days").days) : 0;
    const remainingDays = daysUntil(selectedRow._originalData?.final_review);
    csvContent += `Time elapsed until final review,${elapsedDays} days (${calculateProgress(selectedRow._originalData?.added_to_red_channel, selectedRow._originalData?.final_review)}%)\n`;
    csvContent += `Time remaining until final review,${remainingDays} days\n\n`;
    
    // Change Implementation Section
    csvContent += "CHANGE IMPLEMENTATION STATISTICS (SINCE ON RED CHANNEL)\n";
    csvContent += `Total Changes,${statisticsData?.total || 0}\n`;
    csvContent += `Changes Implemented Successfully,${statisticsData?.successful || 0}\n`;
    csvContent += `Success Rate (%),${statisticsData?.successful_percent ? statisticsData.successful_percent.toFixed(1) : "0"}\n\n`;
    
    // Incident Details Section
    csvContent += "INCIDENT DETAILS (SINCE ON RED CHANNEL)\n";
    csvContent += `P1/P2 Incidents,${incidentCounts?.total || 0}\n\n`;
    
    // P1/P2 Incident Details
    if (incidentCounts?.p1p2 && incidentCounts.p1p2.length > 0) {
      csvContent += "P1/P2 INCIDENT DETAILS\n";
      csvContent += "Incident ID,Affected CI,Opened At,Short Description\n";
      incidentCounts.p1p2.forEach((incident) => {
        const id = incident.id || "N/A";
        const affectedCi = (incident.affected_ci || "N/A").replace(/,/g, ";");
        const openedAt = formatIncidentDate(incident.opened_at);
        const description = (incident.short_description || "N/A").replace(/,/g, ";").replace(/"/g, '""').replace(/\n/g, " ");
        csvContent += `${id},${affectedCi},${openedAt},"${description}"\n`;
      });
      csvContent += "\n";
    }
    
    // Preventive Measures Section
    if (postmortemPreventiveMeasures && postmortemPreventiveMeasures.length > 0) {
      csvContent += "POSTMORTEM PREVENTIVE MEASURES\n";
      csvContent += "Type,ID,Status,Assignee,Due Date,Description\n";
      postmortemPreventiveMeasures.forEach((measure) => {
        const type = measure.type || "N/A";
        const id = type === "jira" ? (measure.jiraid || "N/A") : type === "ptask" ? (measure.ptaskid || "N/A") : "N/A";
        const status = (measure.status || "N/A").replace(/,/g, ";");
        const assignee = (measure.assignee || "N/A").replace(/,/g, ";");
        const dueDate = measure.due_date ? formatDate(measure.due_date) : "N/A";
        const description = (measure.description || "No description provided").replace(/,/g, ";").replace(/\n/g, " ");
        csvContent += `${type},${id},${status},${assignee},${dueDate},"${description}"\n`;
      });
      csvContent += "\n";
    }
    
    // Comments Section
    if (selectedRow.comments && selectedRow.comments !== "-") {
      csvContent += "COMMENTS\n";
      const comments = (selectedRow.comments || "").replace(/,/g, ";").replace(/"/g, '""');
      csvContent += `"${comments}"\n`;
    }

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    const filename = `Statistics_${selectedRow.app_id}_${selectedRow.latest_incident}_${DateTime.now().toFormat("yyyy-MM-dd_HH-mm-ss")}.csv`;
    link.setAttribute("href", url);
    link.setAttribute("download", filename);
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <McModal
      heading={selectedRow ? `${selectedRow.app_name} - Details` : "Details"}
      dimension="large"
      padding="default"
      width="50%"
      open={isOpen}
      closed={onClose}
      closing={onClose}
      zindex={999}
      style={{ "--mc-modal-footer-background": "white" }}
    >
      {loading ? (
        <div style={{ textAlign: "center", padding: "2rem" }}>
          <mc-loading-indicator></mc-loading-indicator>
        </div>
      ) : selectedRow ? (
        <div style={{ display: "flex", flexDirection: "column", height: "100%", maxHeight: "70vh" }}>
          {/* Tabs - Fixed at top */}
          <div style={{ flexShrink: 0, borderBottom: "1px solid #e0e0e0", backgroundColor: "white" }}>
            <McTabBar tabchange={handleTabChange} currentindex={activeTab}>
              <McTab slot="tab" label="Statistics" active={activeTab === 0} />
              <McTab slot="tab" label="Documents" active={activeTab === 1} />
            </McTabBar>
          </div>
          
          {/* Scrollable content area */}
          <div style={{ flex: 1, overflowY: "auto", overflowX: "hidden" }}>
            {activeTab === 0 && (
              <div style={{ display: "flex", flexDirection: "column", gap: "24px", paddingTop: "24px"}}>
          {/* Header */}
          <div style={{ display: "flex", gap: "16px", alignItems: "center", paddingBottom: "16px", borderBottom: "1px solid #e0e0e0" }}>
            <div>
              <div style={{ fontSize: "0.875rem", color: "#666", marginBottom: "4px" }}>
                App ID: <span style={{ fontWeight: "600", color: "#141414" }}>{selectedRow.app_id}</span>
              </div>
              <div style={{ fontSize: "0.875rem", color: "#666", marginBottom: "4px" }}>
                App Instance Name: <span style={{ fontWeight: "600", color: "#141414" }}>{selectedRow.app_name}</span>
              </div>
              <div style={{ fontSize: "0.875rem", color: "#666", marginBottom: "4px" }}>
                Latest Incident: {selectedRow.latest_incident && selectedRow.latest_incident !== "-" ? (
                  <a
                    href={`https://maersk.service-now.com/now/nav/ui/classic/params/target/incident.do%3Fsysparm_query%3Dnumber%3D${selectedRow.latest_incident}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ fontWeight: "600", color: "#0077b2", textDecoration: "underline", cursor: "pointer" }}
                  >
                    {selectedRow.latest_incident}
                  </a>
                ) : (
                  <span style={{ fontWeight: "600", color: "#141414" }}>{selectedRow.latest_incident}</span>
                )}
              </div>
            </div>
            <div style={{ marginLeft: "auto" }}>
              <mc-tag appearance={selectedRow.status === "Red" ? "error" : "success"} fit="small">
                {selectedRow.status} Channel
              </mc-tag>
            </div>
          </div>

          {/* Green Channel Readiness */}
          {(() => {
            const readiness = determineGreenChannelReadiness();
            return (
              <div style={{ 
                padding: "20px", 
                backgroundColor: readiness.ready ? "#e8f5e9" : "#fff3e0", 
                borderRadius: "8px",
                border: readiness.ready ? "1px solid #c8e6c9" : "1px solid #ffe0b2"
              }}>
                <h3 style={{ margin: "0 0 12px 0", fontSize: "1rem", fontWeight: "600", color: "#141414" }}>
                  Green Channel Readiness
                </h3>
                <div style={{ display: "flex", alignItems: "flex-start", gap: "12px" }}>
                  <mc-icon 
                    icon={readiness.ready ? "check-circle" : "exclamation-circle"} 
                    style={{ 
                      color: readiness.ready ? "#2e7d32" : "#f57c00",
                      fontSize: "1.5rem",
                      marginTop: "2px"
                    }}
                  ></mc-icon>
                  <div>
                    <div style={{ 
                      fontSize: "0.938rem", 
                      fontWeight: "600", 
                      color: readiness.ready ? "#2e7d32" : "#e65100",
                      marginBottom: "8px"
                    }}>
                      {readiness.message}
                    </div>
                    {readiness.reasons && readiness.reasons.length > 0 && (
                      <div style={{ fontSize: "0.875rem", color: "#666" }}>
                        {readiness.reasons.map((reason, index) => (
                          <div key={index} style={{ marginBottom: index < readiness.reasons.length - 1 ? "4px" : "0" }}>
                            {reason}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })()}

          {/* Change Implementation Duration */}
          <div style={{ padding: "20px", backgroundColor: "#e3f2fd", borderRadius: "8px" }}>
          
            <h3 style={{ margin: "0 0 16px 0", fontSize: "1rem", fontWeight: "600", color: "#141414" }}>
              Red Channel Summary
            </h3>
            <div style={{ display: "flex", gap: "24px", marginBottom: "12px" }}>
              <div>
                <div style={{ fontSize: "0.875rem", color: "#666", marginBottom: "4px" }}>Oldest Incident Date</div>
                <div style={{ fontSize: "0.875rem", fontWeight: "600", color: "#141414" }}>
                  {oldestIncidentDate ? oldestIncidentDate.replace(/-/g, "/") : "N/A"}
                </div>
              </div>
              <div>
                <div style={{ fontSize: "0.875rem", color: "#666", marginBottom: "4px" }}>Added to Red Channel</div>
                <div style={{ fontSize: "0.875rem", fontWeight: "600", color: "#141414" }}>
                  {formatDate(selectedRow._originalData?.added_to_red_channel)}
                </div>
              </div>
              <div>
                <div style={{ fontSize: "0.875rem", color: "#666", marginBottom: "4px" }}>Final Review</div>
                <div style={{ fontSize: "0.875rem", fontWeight: "600", color: "#141414" }}>
                  {formatDate(selectedRow._originalData?.final_review)}
                </div>
              </div>
              <div>
                <div style={{ fontSize: "0.875rem", color: "#666", marginBottom: "4px" }}>Duration on Red Channel</div>
                <div style={{ fontSize: "0.875rem", fontWeight: "600", color: "#141414" }}>
                  {calculateDuration(selectedRow._originalData?.added_to_red_channel)}
                </div>
              </div>
            </div>
            <div style={{ marginTop: "16px" }}>
              {selectedRow._originalData?.final_review ? (
                <>
                  <div style={{ display: "flex", marginBottom: "8px" }}>
                    <span style={{ fontSize: "0.875rem", color: "#666" }}>
                      Time elapsed until final review: {calculateProgress(selectedRow._originalData?.added_to_red_channel, selectedRow._originalData?.final_review)}%
                    </span>
                    <span style={{ fontSize: "0.875rem", color: "#666" }}>
                       &nbsp;({daysUntil(selectedRow._originalData?.final_review)} days remaining)
                    </span>
                  </div>
                  <div style={{ width: "100%", height: "8px", backgroundColor: "#b3e5fc", borderRadius: "4px", overflow: "hidden" }}>
                    <div
                      style={{
                        width: `${calculateProgress(selectedRow._originalData?.added_to_red_channel, selectedRow._originalData?.final_review)}%`,
                        height: "100%",
                        backgroundColor: "#0277bd",
                        transition: "width 0.3s ease",
                      }}
                    />
                  </div>
                </>
              ) : (
                <div style={{ fontSize: "0.875rem", color: "#666" }}>
                  Time elapsed until final review: <span style={{ fontWeight: "600", color: "#141414" }}>N/A</span>
                </div>
              )}
            </div>
          </div>

          {/* Success Rate */}
          <div style={{ padding: "20px", backgroundColor: "#e3f2fd", borderRadius: "8px" }}>
            <h3 style={{ margin: "0 0 16px 0", fontSize: "1rem", fontWeight: "600", color: "#141414" }}>
              Change Implementation Success Rate (Since on Red Channel)
            </h3>
            {statisticsData ? (
              <>
                <div style={{ display: "flex", gap: "24px", marginBottom: "12px" }}>
                  <div>
                    <div style={{ fontSize: "0.875rem", color: "#666", marginBottom: "4px" }}>Total Changes</div>
                    <div style={{ fontSize: "1.25rem", fontWeight: "600", color: "#141414" }}>{statisticsData.total || 0}</div>
                  </div>
                  <div>
                    <div style={{ fontSize: "0.875rem", color: "#666", marginBottom: "4px" }}>Implemented Successfully</div>
                    <div style={{ fontSize: "1.25rem", fontWeight: "600", color: "#141414" }}>{statisticsData.successful || 0}</div>
                  </div>
                  <div>
                    <div style={{ fontSize: "0.875rem", color: "#666", marginBottom: "4px" }}>Success Rate</div>
                    <div style={{ fontSize: "1.25rem", fontWeight: "600", color: "#0277bd" }}>
                      {statisticsData.successful_percent ? `${statisticsData.successful_percent.toFixed(1)}%` : "0%"}
                    </div>
                  </div>
                </div>
                <div style={{ marginTop: "16px" }}>
                  <div style={{ width: "100%", height: "8px", backgroundColor: "#b3e5fc", borderRadius: "4px", overflow: "hidden" }}>
                    <div
                      style={{
                        width: `${statisticsData.successful_percent || 0}%`,
                        height: "100%",
                        backgroundColor: "#0277bd",
                        transition: "width 0.3s ease",
                      }}
                    />
                  </div>
                </div>
              </>
            ) : (
              <div style={{ textAlign: "center", color: "#666", padding: "1rem" }}>No statistics data available</div>
            )}
          </div>

          {/* Incident Details */}
          <div style={{ padding: "20px", backgroundColor: "#e3f2fd", borderRadius: "8px"}}>
            <div style={{ marginBottom: "16px" }}>
              <h3 style={{ margin: "0", fontSize: "1rem", fontWeight: "600", color: "#141414" }}>
                P1/P2 Incident Details for {selectedRow?.app_name || "Application"} (Since on Red Channel)
              </h3>
            </div>
            {incidentCounts ? (
              <>
                <div style={{ display: "flex", gap: "24px", justifyContent: "center", marginBottom: "16px" }}>
                  <div style={{ textAlign: "center", padding: "12px 24px",  borderRadius: "8px", minWidth: "200px" }}>
                    <div style={{ fontSize: "0.875rem", color: "#666", marginBottom: "8px" }}>Incident Count</div>
                    <div style={{ fontSize: "2rem", fontWeight: "700", color: (incidentCounts.total || 0) === 0 ? "#666" : "#0277bd" }}>{incidentCounts.total || 0}</div>
                  </div>
                </div>
                
                {incidentCounts?.p1p2?.length > 0 && (
                  <div style={{ display: "flex", justifyContent: "center", marginBottom: expandedIncidents ? "20px" : "0" }}>
                    <McButton 
                      appearance="primary"
                      variant="plain"
                      label='Details'
                      trailingIcon={expandedIncidents ? "chevron-up" : "chevron-down"}
                      fit="small" 
                      onClick={() => setExpandedIncidents(!expandedIncidents)}
                    >
            
                    </McButton>
                  </div>
                )}
              
                {/* Expanded Incident Details Table */}
                {expandedIncidents && (
                  <div style={{ marginTop: "20px" }}>
                    {/* P1/P2 Incidents */}
                    {incidentCounts.p1p2 && incidentCounts.p1p2.length > 0 && (
                      <div>
                        <h4 style={{ margin: "0 0 12px 0", fontSize: "0.875rem", fontWeight: "600", color: "#666" }}>
                          P1/P2 Incidents ({incidentCounts.p1p2.length})
                        </h4>
                        <div style={{ backgroundColor: "white", borderRadius: "8px", overflow: "hidden", border: "1px solid #e0e0e0" }}>
                          <table style={{ width: "100%", borderCollapse: "collapse" }}>
                            <thead>
                              <tr style={{ backgroundColor: "#f5f5f5" }}>
                                <th style={{ padding: "12px", textAlign: "left", fontSize: "0.75rem", fontWeight: "600", color: "#666", borderBottom: "1px solid #e0e0e0", width: "15%" }}>Incident ID</th>
                                <th style={{ padding: "12px", textAlign: "left", fontSize: "0.75rem", fontWeight: "600", color: "#666", borderBottom: "1px solid #e0e0e0", width: "20%" }}>Affected CI</th>
                                <th style={{ padding: "12px", textAlign: "left", fontSize: "0.75rem", fontWeight: "600", color: "#666", borderBottom: "1px solid #e0e0e0", width: "15%" }}>Opened At</th>
                                <th style={{ padding: "12px", textAlign: "left", fontSize: "0.75rem", fontWeight: "600", color: "#666", borderBottom: "1px solid #e0e0e0", width: "50%" }}>Short Description</th>
                              </tr>
                            </thead>
                            <tbody>
                              {incidentCounts.p1p2.map((incident, index) => (
                                <tr key={index} style={{ borderBottom: index < incidentCounts.p1p2.length - 1 ? "1px solid #e0e0e0" : "none" }}>
                                  <td style={{ padding: "12px", fontSize: "0.813rem", color: "#141414" }}>
                                    {incident.id ? (
                                      <a
                                        href={`https://maersk.service-now.com/now/nav/ui/classic/params/target/incident.do%3Fsysparm_query%3Dnumber%3D${incident.id}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        style={{ color: "#0077b2", textDecoration: "underline", cursor: "pointer" }}
                                      >
                                        {incident.id}
                                      </a>
                                    ) : (
                                      "N/A"
                                    )}
                                  </td>
                                  <td style={{ padding: "12px", fontSize: "0.813rem", color: "#141414" }}>{incident.affected_ci || "N/A"}</td>
                                  <td style={{ padding: "12px", fontSize: "0.813rem", color: "#141414" }}>{formatIncidentDate(incident.opened_at)}</td>
                                  <td style={{ padding: "12px", fontSize: "0.813rem", color: "#141414", lineHeight: "1.4" }}>{incident.short_description || "N/A"}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </>
            ) : (
              <div style={{ textAlign: "center", color: "#666", padding: "1rem" }}>No incident data available</div>
            )}
          </div>

          {/* Postmortem Preventive Measures */}
          {postmortemPreventiveMeasures && postmortemPreventiveMeasures.length > 0 && (
            <div style={{ padding: "20px", backgroundColor: "#e3f2fd", borderRadius: "8px" }}>
              <h3 style={{ margin: "0 0 16px 0", fontSize: "1rem", fontWeight: "600", color: "#141414" }}>
                Postmortem Preventive Measures
              </h3>
              <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
                {postmortemPreventiveMeasures.map((measure, index) => {
                  const isJira = measure.type === "jira";
                  const isPTask = measure.type === "ptask";
                  const itemId = isJira ? measure.jiraid : isPTask ? measure.ptaskid : "N/A";
                  const itemLabel = isJira ? "JIRA ID" : isPTask ? "PTASK ID" : "ID";
                  
                  return (
                    <div 
                      key={index}
                      style={{ 
                        padding: "16px", 
                        backgroundColor: "white", 
                        borderRadius: "8px",
                        border: "1px solid #e0e0e0"
                      }}
                    >
                      <div style={{ display: "flex", gap: "16px", alignItems: "flex-start", marginBottom: "12px", flexWrap: "wrap" }}>
                        <div style={{ flex: "0 0 auto" }}>
                          <div style={{ fontSize: "0.75rem", color: "#666", marginBottom: "4px" }}>{itemLabel}</div>
                          <mc-tag appearance="neutral" fit="small">
                            {itemId || "N/A"}
                          </mc-tag>
                        </div>
                        <div style={{ flex: "0 0 auto" }}>
                          <div style={{ fontSize: "0.75rem", color: "#666", marginBottom: "4px" }}>Status</div>
                          <mc-tag 
                            appearance={
                              measure.status?.toLowerCase() === "done" || measure.status?.toLowerCase() === "closed" ? "success" : 
                              measure.status?.toLowerCase() === "in progress" || measure.status?.toLowerCase() === "work in progress" ? "warning" : 
                              "neutral"
                            } 
                            fit="small"
                          >
                            {measure.status || "N/A"}
                          </mc-tag>
                        </div>
                        {measure.due_date && (
                          <div style={{ flex: "0 0 auto" }}>
                            <div style={{ fontSize: "0.75rem", color: "#666", marginBottom: "4px" }}>Due Date</div>
                            <div style={{ fontSize: "0.875rem", fontWeight: "600", color: "#141414" }}>
                              {formatDate(measure.due_date)}
                            </div>
                          </div>
                        )}
                        <div style={{ flex: "1", marginLeft: "auto", textAlign: "right", minWidth: "150px" }}>
                          <div style={{ fontSize: "0.75rem", color: "#666", marginBottom: "4px" }}>Assignee</div>
                          <div style={{ fontSize: "0.875rem", fontWeight: "600", color: "#141414" }}>
                            {measure.assignee || "-"}
                          </div>
                        </div>
                      </div>
                      {measure.description && (
                        <div>
                          <div style={{ fontSize: "0.75rem", color: "#666", marginBottom: "4px" }}>Description</div>
                          <div style={{ fontSize: "0.875rem", color: "#141414", lineHeight: "1.5" }}>
                            {measure.description || "No description provided"}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Comments Section */}
          <div style={{ padding: "20px", backgroundColor: "#e3f2fd", borderRadius: "8px" }}>
            <h3 style={{ margin: "0 0 12px 0", fontSize: "1rem", fontWeight: "600", color: "#141414" }}>
              Comments
            </h3>
            <div style={{ fontSize: "0.875rem", color: "#141414", lineHeight: "1.6", whiteSpace: "pre-wrap" }}>
              {selectedRow.comments && selectedRow.comments !== "-" ? selectedRow.comments : "N/A"}
            </div>
          </div>
        </div>
            )}
            
            {activeTab === 1 && (
              <div style={{ paddingTop: "24px" }}>
                <DocumentsTab 
                  appName={selectedRow.app_name}
                  channel={selectedRow.status === "Green" ? "green" : "red"}
                  documents={documents}
                  isLoading={documentsLoading}
                  onRefresh={loadDocuments}
                  userRole={userRole}
                  userEmail={userEmail}
                />
              </div>
            )}
          </div>
        </div>
      ) : (
        <div style={{ textAlign: "center", padding: "2rem", color: "#666" }}>No data available</div>
      )}

      {activeTab === 0 && selectedRow && !loading ? (
        <McButton trailingicon="tray-arrow-down" slot="secondaryAction" appearance="neutral" fit="medium" click={handleDownloadStatistics} disabled={loading || !selectedRow}>
          <span slot="leadingicon">
            <mc-icon icon="tray-arrow-down"></mc-icon>
          </span>
          Download
        </McButton>
      ) : null}
     
    </McModal>
  );
}
